import { useCallback, useState } from "react";
import { parseEther } from "viem";

export const SILO_REWARD_CALCULATOR_API_URL =
  import.meta.env.VITE_SILO_REWARD_CALCULATOR_API_URL || "http://127.0.0.1:3006/api/reward-calculator";

export type SiloRewardCalculatorResult = {
  input?: { stakeAmount?: number | string; storage?: number; uptimePercent?: number };
  assumptions?: {
    distributionIntervalHours?: number;
    distributionCron?: string;
    uptimePercentUsed?: number;
    comparedAgainstNodes?: number;
    weightConfig?: { storage?: number; uptime?: number; stake?: number };
  };
  freshUndistributedRewards?: { wei?: string; eth?: string };
  estimatedEarnings?: {
    hourlyEquivalent?: { wei?: string; eth?: string };
    weekly?: { wei?: string; eth?: string };
    monthly?: { wei?: string; eth?: string };
    yearly?: { wei?: string; eth?: string };
  };
  distributionBreakdown?: {
    liveNodesOnly?: Array<{
      nodeId?: string;
      rewardAmount?: { wei?: string; eth?: string };
      weightScore?: number;
      storage?: number;
      uptime?: number;
      stake?: number;
    }>;
    withInputNode?: Array<{
      nodeId?: string;
      rewardAmount?: { wei?: string; eth?: string };
      weightScore?: number;
      storage?: number;
      uptime?: number;
      stake?: number;
    }>;
    remainderAfterInputNode?: { wei?: string; eth?: string };
  };
};

export function useSiloRewardCalculator() {
  const [stakeAmount, setStakeAmount] = useState("");
  const [storage, setStorage] = useState("");
  const [storageUnit, setStorageUnit] = useState<"gb" | "tb" | "pb">("gb");
  const [uptimePercent, setUptimePercent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SiloRewardCalculatorResult | null>(null);

  const calculate = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const stakeTrimmed = stakeAmount.trim();
      if (stakeTrimmed === "") {
        setError("Stake amount is required and must be >= 0");
        return;
      }

      let stakeWei: bigint;
      try {
        stakeWei = parseEther(stakeTrimmed as `${number}`);
      } catch {
        setError("Stake amount is invalid");
        return;
      }
      if (stakeWei < 0n) {
        setError("Stake amount is required and must be >= 0");
        return;
      }

      const storageValue = Number(storage);
      const uptime = uptimePercent.trim() === "" ? undefined : Number(uptimePercent);

      if (!Number.isFinite(storageValue) || storageValue < 0) {
        setError("Storage is required and must be >= 0");
        return;
      }
      if (uptime !== undefined && (!Number.isFinite(uptime) || uptime < 0 || uptime > 100)) {
        setError("Uptime percent must be between 0 and 100");
        return;
      }

      const storageMultiplier =
        storageUnit === "gb" ? 1024 ** 3 : storageUnit === "tb" ? 1024 ** 4 : 1024 ** 5;
      const storageBytes = Math.floor(storageValue * storageMultiplier);

      const body: { stakeAmount: string; storage: number; uptimePercent?: number } = {
        stakeAmount: stakeWei.toString(),
        storage: storageBytes,
      };
      if (uptime !== undefined) body.uptimePercent = uptime;

      const response = await fetch(SILO_REWARD_CALCULATOR_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.error || json?.message || `Calculator failed (${response.status})`);
      }
      setResult(json as SiloRewardCalculatorResult);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : "Calculator request failed");
    } finally {
      setLoading(false);
    }
  }, [stakeAmount, storage, storageUnit, uptimePercent]);

  return {
    stakeAmount,
    setStakeAmount,
    storage,
    setStorage,
    storageUnit,
    setStorageUnit,
    uptimePercent,
    setUptimePercent,
    loading,
    error,
    result,
    calculate,
  };
}
